import { emptyRewardsState, reservedRewardPoints, type RewardsState } from "@/lib/loyalty-rewards"
import { PaymentMethod, ServiceType } from "@/types"
import { LoyaltyProgram } from "@/types/loyalty"
import { ActiveBenefits, EntitlementGrant } from "@/types/loyalty-benefits"
import type { SchedulingSlot, ScheduledPaymentPolicy } from "@/lib/scheduling/types"
import type { OrderTiming } from "@/types/external/order-request"
import { atomWithStorage } from "jotai/utils"

export { formatCouponAvailabilityMessage, isCouponAvailableNow } from "@/lib/coupon-availability"

export const STEP_ORDER = ["timing", "service", "schedule", "payment", "extras", "review"] as const
export type CheckoutStep = (typeof STEP_ORDER)[number]

export const STEP_INDEX: Record<CheckoutStep, number> = {
  timing: 0,
  service: 1,
  schedule: 2,
  payment: 3,
  extras: 4,
  review: 5,
}

export const STEP_LABELS: Record<CheckoutStep, string> = {
  timing: "Quando receber",
  service: "Como receber",
  schedule: "Horário",
  payment: "Pagamento",
  extras: "Cupom e pontos",
  review: "Revisar e finalizar",
}

/** What the wizard needs to know to decide which steps exist for this order. */
export type VisibleStepsOptions = {
  hasExtras: boolean
  isTableOrder?: boolean
  showTiming?: boolean
  showSchedule?: boolean
}

/**
 * Steps the wizard actually shows. Table mode shows only "review" (service and
 * payment are pre-filled), "extras" needs coupons or loyalty, "timing" needs
 * both modalities at once, and "schedule" only exists for a scheduled order on
 * a channel that takes scheduling.
 */
export function getVisibleSteps({
  hasExtras,
  isTableOrder = false,
  showTiming = false,
  showSchedule = false,
}: VisibleStepsOptions): CheckoutStep[] {
  if (isTableOrder) return ["review"]
  return STEP_ORDER.filter((step) => {
    if (step === "extras") return hasExtras
    if (step === "timing") return showTiming
    if (step === "schedule") return showSchedule
    return true
  })
}

/**
 * Whether the slot picker is a step of its own for this state: a scheduled
 * order whose chosen channel takes scheduling (before a channel is chosen, any
 * schedulable channel keeps the step in the stepper).
 */
export function isScheduleStepVisible(
  state: Pick<CheckoutState, "orderTiming" | "serviceType">,
  schedulableServiceTypes: ServiceType[],
): boolean {
  if (state.orderTiming !== "SCHEDULED") return false
  if (!state.serviceType) return schedulableServiceTypes.length > 0
  return schedulableServiceTypes.includes(state.serviceType)
}

export type LoyaltyState = {
  use: boolean
  pointsBalance: number
  pointsToUse: number
  program: LoyaltyProgram
}

export type BenefitsState = {
  active: ActiveBenefits | null
  available: EntitlementGrant[]
  selectedIds: string[]
  /**
   * Bag lines this checkout added on the customer's behalf when they claimed a
   * free-product reward, by grant id. Dropping the reward takes its line out of
   * the bag again; a line the customer had added themselves is never recorded
   * here, so it stays. Optional: sessions stored before this existed restore
   * without it.
   */
  freeProductLines?: Record<string, string>
}

export type CheckoutState = {
  /** Modality of the order; SCHEDULED always carries a `scheduledSlot`. */
  orderTiming: OrderTiming
  /** Slot chosen from the scheduling context; `readyAt` is the store instant. */
  scheduledSlot: SchedulingSlot | null
  /** Arrival window promised to the customer; full model only (D3). */
  scheduledArrival: { start: string; end: string } | null
  /** Calendar day of the chosen slot; full model only, drives the day chips. */
  scheduledDate: string | null
  /** Payment policy of the chosen channel while scheduling (D12). */
  scheduledPayment: ScheduledPaymentPolicy | null
  serviceType?: ServiceType
  addressId?: string
  servicePrice: number
  paymentMethod?: PaymentMethod
  selectedMethodInfo?: string
  selectedPaymentMethodId?: string
  changeFor: number | null
  /**
   * Coupon codes the customer typed, in the order they typed them. What each
   * is worth is never stored here: coupon-service evaluates the codes against
   * the bag and the checkout renders only that answer.
   */
  couponCodes: string[]
  loyalty: LoyaltyState
  benefits: BenefitsState
  /** Items redeemable with points and the ones picked; optional for sessions stored before rewards. */
  rewards?: RewardsState
  specialInstructions: string
  /** Order-update WhatsApp box as the customer left it on this order; absent follows the saved choice (on by default). */
  notifyStatusWhatsapp?: boolean
  /**
   * When true and paymentMethod is PIX_ONLINE, the fixed PIX service fee is
   * added to the total and embutida as SERVICE_FEE on the order.
   * Source: GET /public/.../available-payment-methods.
   */
  pixOnlinePassFeeToCustomer: boolean
  /** Fee in major units (BRL), e.g. 0.60. Derived from pixOnlineServiceFeeMinor. */
  pixServiceFee: number
}

/** Default fee mirrors payment-service-core PixServiceFeeMinor (R$ 0,60). */
export const DEFAULT_PIX_SERVICE_FEE = 0.6

export const initialCheckoutState: CheckoutState = {
  orderTiming: "INSTANT",
  scheduledSlot: null,
  scheduledArrival: null,
  scheduledDate: null,
  scheduledPayment: null,
  serviceType: undefined,
  addressId: undefined,
  servicePrice: 0,
  paymentMethod: undefined,
  selectedMethodInfo: undefined,
  selectedPaymentMethodId: undefined,
  changeFor: null,
  couponCodes: [],
  loyalty: {
    use: false,
    pointsBalance: 0,
    pointsToUse: 0,
    program: {
      id: "",
      merchantId: "",
      name: "",
      description: "",
      pointsPerCurrency: 0,
      minimumPointsToRedeem: 0,
      pointsValueInCurrency: 0,
      pointsExpirationDays: 0,
      active: false,
    },
  },
  benefits: {
    active: null,
    available: [],
    selectedIds: [],
    freeProductLines: {},
  },
  rewards: emptyRewardsState,
  specialInstructions: "",
  pixOnlinePassFeeToCustomer: false,
  pixServiceFee: DEFAULT_PIX_SERVICE_FEE,
}

/** Where a half-filled wizard survives a reload; cleared when checkout unmounts. */
export const CHECKOUT_SESSION_KEY = "meucontroleai:checkout-wizard"

type StoredCheckoutState = Partial<CheckoutState> & { coupon?: unknown }

function isRestorable(value: unknown): value is StoredCheckoutState {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

/**
 * A session stored before multi-coupon carried one `coupon: {code}`; it comes
 * back as a one-code list, and any amount it held is dropped — coupon-service
 * evaluates it again.
 */
function restoredCouponCodes(codes: unknown, legacy: unknown): string[] {
  if (Array.isArray(codes)) return codes.filter((code): code is string => typeof code === "string")
  if (typeof legacy === "object" && legacy !== null && "code" in legacy && typeof legacy.code === "string") {
    return [legacy.code]
  }
  return []
}

/**
 * sessionStorage, not localStorage: the wizard is one visit's work, and a bag
 * paid for yesterday must not repopulate a payment method today. Every access
 * is guarded because Safari's private mode throws on the first write.
 */
export const checkoutSessionStorage = {
  getItem(key: string, initialValue: CheckoutState): CheckoutState {
    try {
      const raw = window.sessionStorage.getItem(key)
      if (!raw) return initialValue
      const parsed: unknown = JSON.parse(raw)
      if (!isRestorable(parsed)) return initialValue
      const { coupon, ...rest } = parsed
      return { ...initialValue, ...rest, couponCodes: restoredCouponCodes(parsed.couponCodes, coupon) }
    } catch {
      return initialValue
    }
  },
  setItem(key: string, value: CheckoutState): void {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value))
    } catch {
      return
    }
  },
  removeItem(key: string): void {
    try {
      window.sessionStorage.removeItem(key)
    } catch {
      return
    }
  },
}

/**
 * `getOnInit` so the very first render already sees the restored state: the
 * entry guard runs in that render, and an empty state there bounces the
 * customer back to the first step before the value could arrive.
 */
export const checkoutAtom = atomWithStorage<CheckoutState>(
  CHECKOUT_SESSION_KEY,
  initialCheckoutState,
  checkoutSessionStorage,
  { getOnInit: true },
)

/**
 * Switches modality, dropping everything the previous one decided: a slot and
 * a payment chosen for a scheduled order mean nothing for an immediate one,
 * and the reverse (D12, §9.2 item 1).
 */
export function applyOrderTiming(state: CheckoutState, timing: OrderTiming): CheckoutState {
  if (state.orderTiming === timing) return state
  return {
    ...state,
    orderTiming: timing,
    scheduledSlot: null,
    scheduledArrival: null,
    scheduledDate: null,
    scheduledPayment: null,
    paymentMethod: undefined,
    selectedMethodInfo: undefined,
    selectedPaymentMethodId: undefined,
    changeFor: null,
  }
}

/** Whether the PIX Online service fee applies to the current payment selection. */
export function shouldApplyPixServiceFee(state: CheckoutState): boolean {
  return (
    state.paymentMethod === "PIX_ONLINE" &&
    state.pixOnlinePassFeeToCustomer &&
    (state.pixServiceFee ?? 0) > 0
  )
}

/** Service fee amount in BRL major units when applicable; otherwise 0. */
export function computePixServiceFee(state: CheckoutState): number {
  return shouldApplyPixServiceFee(state) ? state.pixServiceFee : 0
}

export function selectedEntitlements(state: CheckoutState): EntitlementGrant[] {
  const selected = new Set(state.benefits.selectedIds)
  return state.benefits.available.filter((g) => selected.has(g.grantId))
}

/**
 * What the customer actually pays for items: the bag minus what the loyalty
 * benefits already take off it. Both points and coupons are measured against
 * this, never against the raw bag total.
 *
 * A free-product reward is an ordinary bag line at full price plus an ITEM
 * discount that cancels it (Open Delivery has no free-item concept), so the raw
 * bag total let the gift widen every other discount: it pushed the points
 * maximum up by the gift's price and gave a percentage coupon a slice of it —
 * money the customer never owed, and an order discounted past what it costs.
 */
export function computePayableItemsSubtotal(cartTotal: number, benefitItemsDiscount = 0): number {
  return Math.max(0, cartTotal - benefitItemsDiscount)
}

/** Points left for the points-as-money redemption once the picked rewards hold theirs. */
export function availableLoyaltyPoints(state: CheckoutState): number {
  return Math.max(0, state.loyalty.pointsBalance - reservedRewardPoints(state.rewards ?? emptyRewardsState))
}

export function computeMaxRedeemableLoyaltyPoints(state: CheckoutState, payableItems: number): number {
  const value = state.loyalty.program.pointsValueInCurrency
  if (value <= 0) return 0
  const maxByCart = Math.floor(payableItems / value)
  return Math.min(availableLoyaltyPoints(state), maxByCart)
}

export function canRedeemLoyaltyPoints(state: CheckoutState, payableItems: number): boolean {
  const { program } = state.loyalty
  if (!program.active) return false
  const max = computeMaxRedeemableLoyaltyPoints(state, payableItems)
  return max > 0 && max >= program.minimumPointsToRedeem
}

export function clampLoyaltyPoints(state: CheckoutState, payableItems: number, points: number): number {
  const max = computeMaxRedeemableLoyaltyPoints(state, payableItems)
  const min = state.loyalty.program.minimumPointsToRedeem
  if (max <= 0 || max < min) return 0
  if (Number.isNaN(points)) return min
  return Math.min(Math.max(Math.round(points), min), max)
}

export function computeLoyaltyPointsUsed(state: CheckoutState, payableItems: number): number {
  if (!state.loyalty.use) return 0
  return clampLoyaltyPoints(state, payableItems, state.loyalty.pointsToUse)
}

export function computeLoyaltyDiscount(state: CheckoutState, payableItems: number): number {
  return computeLoyaltyPointsUsed(state, payableItems) * state.loyalty.program.pointsValueInCurrency
}

/**
 * Fallback total for callers without the derived pricing. Coupons are left
 * out on purpose: their value only exists in coupon-service's answer, which
 * deriveCheckoutPricing folds in.
 */
export function computeTotal(
  state: CheckoutState,
  cartTotal: number,
  benefitTotal = 0,
  payableItems = cartTotal,
  couponDiscount = 0,
): number {
  const loyaltyDiscount = computeLoyaltyDiscount(state, payableItems)
  const pixServiceFee = computePixServiceFee(state)
  return Math.max(
    0,
    cartTotal + state.servicePrice + pixServiceFee - couponDiscount - loyaltyDiscount - benefitTotal,
  )
}

type GuardContext = {
  state: CheckoutState
  cartTotal: number
  hasDeliverableAddress: (addressId: string) => boolean
  /** True when the customer is in table/dine-in mode (scanned a QR code). */
  isTableOrder?: boolean
  /** True when the wizard shows the schedule step, which then demands a slot. */
  scheduleStepVisible?: boolean
  /**
   * What the customer pays after every discount, when the caller already derived
   * it (deriveCheckoutPricing). The change-for check needs the same figure the
   * screens quote; without it this falls back to a total that ignores the
   * loyalty benefits.
   */
  orderTotal?: number
}

export type StepValidation = {
  ok: boolean
  hint?: string
}

export const stepGuards: Record<CheckoutStep, (ctx: GuardContext) => StepValidation> = {
  timing: () => ({ ok: true }),
  service: ({ state, hasDeliverableAddress, isTableOrder }) => {
    if (isTableOrder) return { ok: true }
    if (!state.serviceType) {
      return { ok: false, hint: "Escolha entrega ou retirada para continuar" }
    }
    if (state.serviceType === "DELIVERY") {
      if (!state.addressId) {
        return { ok: false, hint: "Escolha um endereço para receber" }
      }
      if (!hasDeliverableAddress(state.addressId)) {
        return { ok: false, hint: "Endereço fora da área de entrega. Escolha outro" }
      }
    }
    return { ok: true }
  },
  schedule: ({ state, scheduleStepVisible, isTableOrder }) => {
    if (isTableOrder || !scheduleStepVisible) return { ok: true }
    if (!state.scheduledSlot) {
      return { ok: false, hint: "Escolha o horário do agendamento para continuar" }
    }
    return { ok: true }
  },
  payment: ({ state, cartTotal, isTableOrder, orderTotal }) => {
    if (isTableOrder) return { ok: true }
    if (!state.paymentMethod) {
      return { ok: false, hint: "Escolha como deseja pagar para continuar" }
    }
    if (state.paymentMethod === "CASH" && state.changeFor !== null) {
      const total = orderTotal ?? computeTotal(state, cartTotal)
      if (state.changeFor < total) {
        return { ok: false }
      }
    }
    return { ok: true }
  },
  extras: () => ({ ok: true }),
  review: () => ({ ok: true }),
}

export function firstInvalidStep(ctx: GuardContext): CheckoutStep | null {
  for (const step of STEP_ORDER) {
    const validation = stepGuards[step](ctx)
    if (!validation.ok) return step
  }
  return null
}
